import React, { useCallback, useContext, useMemo, useState } from 'react';
import './Modal.css';
import Modal from 'react-modal';
import useModalContent from 'modules/Modal/useModalContent';
import Image from 'components/Image';
import Button from 'components/Button/Button';
import { useFirebaseProviders } from 'modules/Firebase/FirebaseProvider';
import DogAvatar from 'components/DogAvatar/DogAvatar';
import TextInput from 'components/TextInput/TextInput';

const MODAL_SELECTOR = '#rootModal';
Modal.setAppElement(document.querySelector(MODAL_SELECTOR));

const ModalContext = React.createContext(null);
const modalStyle = {
    overlay: {
        background: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(10px)',
    },
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
    },
};

function ModalProvider({ children }) {
    const [modalContent, setModalContent] = useState(null);
    const { isOpen, open, close } = useModalContent(setModalContent);

    const modalActions = useMemo(() => ({ open, close }), [open, close]);

    return (
        <>
            <ModalContext.Provider value={modalActions}>{children}</ModalContext.Provider>
            <div className={'modal'}>
                <Modal
                    parentSelector={() => document.querySelector(MODAL_SELECTOR)}
                    isOpen={isOpen}
                    onRequestClose={close}
                    style={modalStyle}
                >
                    {modalContent}
                </Modal>
            </div>
        </>
    );
}

export default ModalProvider;

export function useModal() {
    const { open, close } = useContext(ModalContext);

    const openImageModal = useCallback(
        uri => {
            open(() => <ImageModalContent uri={uri} />);
        },
        [open]
    );

    const openAlertModal = useCallback(
        text => {
            open(() => <AlertModalContent text={text} onClick={close} />);
        },
        [close, open]
    );

    const openReservationFormModal = useCallback(
        (dogId, dogData) => {
            open(() => <ReservationFormContent dogId={dogId} dogData={dogData} close={close} />);
        },
        [close, open]
    );

    return useMemo(
        () => ({ openImageModal, openAlertModal, openReservationFormModal, closeModal: close }),
        [close, openAlertModal, openImageModal, openReservationFormModal]
    );
}

function ImageModalContent({ uri }) {
    return <Image alt={'dog'} className={'modal-image'} src={uri} />;
}

function AlertModalContent({ text, onClick }) {
    return (
        <div className={'modal-alert shadow'}>
            {text}
            <div className={'modal-alert-control'}>
                <Button label={'CLOSE'} onClick={onClick} />
            </div>
        </div>
    );
}

const valuesDefaultState = {
    customerName: '',
    customerEmail: '',
    customerPhone: '',
};

const validationDefaultState = {
    customerName: false,
    customerEmail: false,
    customerPhone: false,
};

function ReservationFormContent({ dogId, dogData, close }) {
    const { avatar: avatarUri, name, price } = dogData || {};
    const { firestore } = useFirebaseProviders();

    const [formState, setFormState] = useState({
        dogName: name,
        price,
        dogId,
        ...valuesDefaultState,
    });
    const [validationState, setValidationState] = useState(validationDefaultState);

    const submitHandler = useCallback(() => {
        if (Object.values(validationState).some(v => !v)) return;
        close();
        firestore
            .collection('reservations')
            .add(formState) // TODO: Add validation
            .catch(error => {
                console.error('Error adding document: ', error);
            });
    }, [close, firestore, formState]);

    return (
        <div className={'modal-reservation background-gradient blur shadow'}>
            <div className={'reservation-form__content'}>
                <div className={'reservation-form__info'}>
                    <DogAvatar avatarUri={avatarUri} />
                </div>
                <div className={'reservation-form__inputs'} data-aos="fade-left">
                    <TextInput label={'Dog Name'} value={name} disabled />
                    <TextInput label={'Price'} value={`$ ${price}`} disabled />
                    <TextInput
                        label={'Your Name'}
                        placeholder={'Name'}
                        onChange={({ value, valid }) => {
                            setFormState({ ...formState, customerName: value });
                            setValidationState({ ...validationState, customerName: valid });
                        }}
                        value={formState.customerName}
                        valid={validationState.customerName}
                        validationRegExp={/^[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$/g}
                    />
                    <TextInput
                        label={'Your Email'}
                        placeholder={'email@domain.com'}
                        onChange={({ value, valid }) => {
                            setFormState({ ...formState, customerEmail: value });
                            setValidationState({ ...validationState, customerEmail: valid });
                        }}
                        value={formState.customerEmail}
                        valid={validationState.customerEmail}
                        validationRegExp={/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g}
                    />
                    <TextInput
                        label={'Your Phone'}
                        placeholder={'(000)000-0000'}
                        onChange={({ value, valid }) => {
                            setFormState({ ...formState, customerPhone: value });
                            setValidationState({ ...validationState, customerPhone: valid });
                        }}
                        value={formState.customerPhone}
                        valid={validationState.customerPhone}
                        validationRegExp={/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/g}
                    />
                    <Button label={'Reserve'} onClick={submitHandler} />
                </div>
            </div>
        </div>
    );
}
